import { Dispatch, SetStateAction } from 'react'

import { signOut, User } from '@firebase/auth'
import { doc, getDoc } from '@firebase/firestore'
import { getDownloadURL, ref } from '@firebase/storage'
import axios from 'axios'

import { auth, db, storage } from 'config/firebase'
import { AuthContextUser } from 'context/auth/types'
import {
	setBusinessAccounts,
	setConfigurationFeedback,
	setConfigurationOrganisation,
	setOrgMembers,
	updateRoutes,
} from 'context/materialUI'
import {
	ContextActionTypes,
	ShortBusinessAccounts,
	ShortOrganisationMembers,
} from 'context/materialUI/types'
import BusinessAccount from 'layouts/business-account'
import OrganisationMemberPage from 'layouts/organisation-member'
import { ROUTE_PATHS, RouteType } from 'routes'
import { fetchFeedbackConfiguration, fetchOrganisationConfiguration } from 'utils/config'
import { fetchFeedbackItems } from 'utils/feedback'
import { ClientData, UserDataResponse } from 'utils/signing/types'

export async function fetchUserData(user: User) {
	const userToken = await user.getIdToken()
	const response = await axios.get(`${process.env.REACT_APP_API_URL}/user`, {
		headers: {
			Authorization: userToken,
		},
	})
	return response.data as UserDataResponse
}

export async function handleUserSigning(
	user: User,
	setUser: Dispatch<SetStateAction<AuthContextUser | null>>
) {
	const userResponse = await fetchUserData(user)

	const logoRef = ref(storage, `organisations/${userResponse.user.orgId}/images/logo.png`)
	const logo = await getDownloadURL(logoRef)

	const docRef = doc(db, 'laurel', userResponse.user.orgId)
	const docSnap = await getDoc(docRef)
	if (!docSnap.exists()) throw new Error('No Organisation Data')

	const orgData = docSnap.data() as { name: string }
	const clientData: ClientData = {
		...userResponse.user,
		name: orgData.name,
		logo: logo,
	}

	const userData = {
		role: clientData.role,
		organisation: {
			id: clientData.orgId,
			logo: clientData.logo,
			name: clientData.name,
			users: clientData?.orgUsers,
		},
		name: userResponse.user.name,
	}

	setUser({
		auth: user,
		data: userData,
	})
	return userData
}

export async function createMemberAndAccountRoutes(
	dispatch: Dispatch<ContextActionTypes>,
	routes: RouteType[],
	orgMembers: ShortOrganisationMembers[],
	businessAccounts: ShortBusinessAccounts[]
) {
	const orgMemberRoutes = orgMembers.map(orgMember => {
		const route: RouteType = {
			type: 'invisible',
			name: `Organisation Member: ${orgMember.id}`,
			key: `organisation-member-${orgMember.id}`,
			route: `${ROUTE_PATHS.ORGANISATION_MEMBER.BASE}/${orgMember.id}`,
			component: <OrganisationMemberPage />,
			authRequired: true,
		}
		return route
	})

	const businessAccountRoutes = businessAccounts.map(account => {
		const route: RouteType = {
			type: 'invisible',
			name: `Business Account: ${account.id}`,
			key: `business-account-${account.id}`,
			route: `${ROUTE_PATHS.BUSINESS_ACCOUNTS.BASE}/${account.id}`,
			component: <BusinessAccount />,
			authRequired: true,
		}
		return route
	})
	const routesToUpdate = [...orgMemberRoutes, ...businessAccountRoutes]
	updateRoutes(dispatch, routes, routesToUpdate)
}

export async function handleClientSigning({
	user,
	dispatch,
	setUser,
	routes,
}: {
	user: User
	dispatch: Dispatch<ContextActionTypes>
	setUser: Dispatch<SetStateAction<AuthContextUser | null>>
	routes: RouteType[]
}) {
	try {
		const [userData, orgConfigResponse, feedbackConfigResponse, feedbackItems] = await Promise.all([
			handleUserSigning(user, setUser),
			fetchOrganisationConfiguration(user),
			fetchFeedbackConfiguration(user),
			fetchFeedbackItems(user),
		])

		setOrgMembers(dispatch, feedbackItems.orgMembers)
		setBusinessAccounts(dispatch, feedbackItems.businessAccounts)

		createMemberAndAccountRoutes(
			dispatch,
			routes,
			feedbackItems.orgMembers,
			feedbackItems.businessAccounts
		)

		setConfigurationOrganisation(dispatch, orgConfigResponse)
		setConfigurationFeedback(dispatch, feedbackConfigResponse)
		return userData
	} catch (error) {
		console.error('Error handling client signing.', error)
		signOut(auth)
		return false
	}
}
